import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Avatar, Button } from '@mui/material';
import { Box, Stack } from '@mui/system';

import WaveUIForm_V2 from '@/shared/components/forms/WaveUIForm_V2';
import FieldType from '@/shared/components/forms/WaveUIForm_V2/types/fieldType.enum';

type NewNoteFormProps = {
  onCreateAgentNote: (note: string) => void;
  onDiscardCreateNote: () => void;
};

const NewNoteForm: FC<NewNoteFormProps> = ({
  onCreateAgentNote,
  onDiscardCreateNote,
}) => {
  const { t } = useTranslation(['notes', 'common']);

  const [note, setNote] = useState('');
  const [error, setError] = useState({ note: '' });

  const onValidate = () => {
    const errors = { note: '' };

    if (!note) {
      errors['note'] = t('notes:note_required');
    } else {
      errors['note'] = '';
    }

    setError(errors);

    return Object.values(errors).some((error) => error !== '');
  };

  return (
    <Stack spacing={2}>
      <Stack direction='row' spacing={2} alignItems='center'>
        <Avatar
          alt={'Agent'}
          src={''}
          sx={{
            width: 48,
            height: 48,
          }}
        />

        <Box
          sx={{
            'backgroundColor': 'grey.100',
            'width': '100%',
            'borderRadius': 2,
            'p': 2,
            'position': 'relative',
            '&::before': {
              color: 'grey.100',
              content: '""',
              position: 'absolute',
              top: '50%',
              left: '-8px',
              transform: 'translateY(-50%)',
              borderWidth: '8px 8px 8px 0',
              borderStyle: 'solid',
              borderColor: 'transparent currentColor transparent transparent',
            },
          }}
        >
          <WaveUIForm_V2
            fields={[
              {
                field_type: FieldType.Text,
                field_props: {
                  label: '',
                  value: note,
                  name: 'note',
                  placeholder: t('notes:note_placeholder'),
                  multiline: true,
                  rows: 4,
                  error: !!error.note,
                  helperText: error.note,
                  sx: {
                    fontSize: '12px',
                    borderRadius: '8px',
                  },
                },
              },
            ]}
            getFieldsValues={(value) => {
              const note = value.note as string;
              setNote(note);
            }}
          />
        </Box>
      </Stack>

      <Stack direction='row' spacing={1} justifyContent='flex-end'>
        <Button variant='outlined' onClick={onDiscardCreateNote}>
          {t('common:discard')}
        </Button>
        <Button
          variant='contained'
          onClick={() => {
            const formHasErrors = onValidate();

            if (formHasErrors) return;

            onCreateAgentNote(note);
            onDiscardCreateNote();
          }}
        >
          {t('common:create')}
        </Button>
      </Stack>
    </Stack>
  );
};

export default NewNoteForm;
